import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS, SUPER_ADMIN_EMAIL } from '../core/config';
import {
  clearAuthSession,
  fetchCurrentUser,
  setAuthSession,
} from '../services/authService';

const AuthContext = createContext(null);

const DEFAULT_USER = {
  id: null,
  name: 'Dhruvi Patel',
  email: 'dhruvi.patel@concettolabs.com',
  plan: 'Free',
  role: 'user',
};

function readStoredUserFallback() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return DEFAULT_USER;
    const parsed = JSON.parse(raw);
    return {
      id: parsed.id ?? null,
      name: parsed.full_name || parsed.name || DEFAULT_USER.name,
      email: parsed.email || DEFAULT_USER.email,
      plan: parsed.plan || DEFAULT_USER.plan,
      role: parsed.role || 'user',
    };
  } catch {
    return DEFAULT_USER;
  }
}

export function deriveDisplayName(email) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized) return 'User';
  if (normalized === SUPER_ADMIN_EMAIL) return 'Super Admin';

  const local = normalized.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (!parts.length) return 'User';

  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(readStoredUserFallback);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback((nextUser) => {
    const normalized = {
      id: nextUser?.id ?? null,
      name: nextUser?.full_name || nextUser?.name || deriveDisplayName(nextUser?.email),
      email: nextUser?.email || DEFAULT_USER.email,
      plan: nextUser?.plan || DEFAULT_USER.plan,
      role: nextUser?.role || 'user',
    };
    setUserState(normalized);
    setAuthSession({
      user: {
        id: normalized.id,
        email: normalized.email,
        full_name: normalized.name,
        plan: normalized.plan,
        role: normalized.role,
      },
    });
    return normalized;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await fetchCurrentUser();
      if (profile) {
        applyUser(profile);
      }
    } catch {
      clearAuthSession();
      setUserState(DEFAULT_USER);
    } finally {
      setLoading(false);
    }
  }, [applyUser]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const setUser = (nextUser) => {
    applyUser(nextUser);
  };

  const logout = () => {
    clearAuthSession();
    setUserState(DEFAULT_USER);
    window.location.replace('/login.html');
  };

  const isSuperAdmin = user.role === 'admin' || user.role === 'super_admin';

  return (
    <AuthContext.Provider value={{ user, isSuperAdmin, setUser, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
